import { Suspense, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Saisons, Schuetzen } from './api/apiClient';
import MitteilungenPopup from './components/MitteilungenPopup';
import { SeiteLaedt } from './utils/lazyPage';
import { ROLLE_ADMINISTRATOR, ROLLE_SCHIESSMEISTER, ROLLE_WETTKAMPFMANAGER, SAISON_STATUS_MELDUNG } from './constants';

// Reihenfolge und Bereiche wie auf der Admin-Übersicht (AdminPage): oft benötigtes
// oben, selten benötigtes unten. Die Trennlinien markieren die Bereichsgrenzen.
const ADMIN_SECTION = {
  label: 'Administration',
  items: [
    { label: 'Übersicht', to: '/admin', icon: GridIcon },
    { separator: true, key: 'admin-sep-saisonbetrieb' },
    { label: 'Saisons', to: '/admin/saisons', icon: CalendarIcon },
    { label: 'Gebühren', to: '/admin/gebuehren', icon: EuroIcon },
    { separator: true, key: 'admin-sep-kommunikation' },
    { label: 'Mitteilungen & Termine', to: '/admin/mitteilungen', icon: BellIcon },
    { label: 'Rundmail', to: '/admin/funktionstraeger-mail', icon: MailIcon },
    { label: 'Dateiablage', to: '/admin/dateien', icon: FolderIcon },
    { separator: true, key: 'admin-sep-vereine-personen' },
    { label: 'Vereine', to: '/admin/vereine', icon: BuildingIcon },
    { label: 'Schützen', to: '/admin/schuetzen', icon: PersonIcon },
    { separator: true, key: 'admin-sep-kontrolle-protokolle' },
    { label: 'Freigabelinks', to: '/admin/freigabelinks', icon: LinkIcon },
    { label: 'Sicherheitsprotokoll', to: '/admin/sicherheitsprotokoll', icon: ShieldIcon },
    { label: 'Änderungsprotokoll', to: '/admin/aenderungsprotokoll', icon: HistoryIcon },
    { separator: true, key: 'admin-sep-einstellungen' },
    { label: 'Benutzer', to: '/admin/benutzereinstellungen', icon: UsersIcon },
    { label: 'Klassen', to: '/admin/klasseneinstellungen', icon: SlidersIcon },
  ],
};

function buildSaisonSection(saisons, activeSaison, ownTeamItems) {
  const sortedSaisons = [...(Array.isArray(saisons) ? saisons : [])]
    .sort((a, b) => Number(b?.saisonId ?? 0) - Number(a?.saisonId ?? 0));
  const oldSaisons = sortedSaisons.filter((saison) => Number(saison?.status) === 40);
  return {
    label: 'Saison',
    to: activeSaison?.path ?? '/saison',
    items: [
      ...(activeSaison ? [{
        label: getSaisonLabel(activeSaison.saisonId),
        to: activeSaison.path,
        items: [
          { label: 'Übersicht', to: activeSaison.path, icon: GridIcon },
          ...(Array.isArray(ownTeamItems) && ownTeamItems.length > 0
            ? [{ label: 'Eigene Mannschaften', items: ownTeamItems }]
            : []),
        ],
      }] : []),
      ...(oldSaisons.length > 0 ? [{
        label: 'Alte Saisons',
        items: oldSaisons.map((saison) => {
          const saisonPath = `/saison/${saison.saisonId}`;
          return {
            label: getSaisonLabel(saison.saisonId),
            to: saisonPath,
            items: [
              { label: 'Übersicht', to: saisonPath, icon: GridIcon },
            ],
          };
        }),
      }] : []),
    ],
  };
}

const WETTKAMPFVERWALTER_SECTION = {
  label: 'Wettkampfverwalter',
  to: '/manager',
  items: [
    { label: 'Ergebniseingabe', to: '/manager', icon: TargetIcon },
  ],
};

const ALLGEMEIN_SECTION = {
  label: 'Allgemein',
  items: [
    { label: 'Übersicht', to: '/allgemein', icon: GridIcon },
    { label: 'Mitteilungen & Termine', to: '/allgemein/mitteilungen', icon: BellIcon },
    { label: 'Kalender', to: '/allgemein/kalender', icon: CalendarIcon },
    { label: 'Downloads', to: '/allgemein/downloads', icon: FolderIcon },
    { label: 'Vereinsinfo', to: '/allgemein/vereinsinfo', icon: InfoIcon },
    { label: 'Über', to: '/allgemein/ueber', icon: AboutIcon },
    // Nutzungsbedingungen vorerst deaktiviert
    // { label: 'Nutzungsbedingungen', to: '/allgemein/nutzungsbedingungen', icon: DocumentIcon },
  ],
};

const MEINE_MANNSCHAFTEN_SECTION = {
  label: 'Meine Mannschaften',
  items: [
    { label: 'Übersicht', to: '/meine-mannschaften', icon: TeamIcon },
  ],
};

const DASHBOARD_SECTION = {
  label: 'Mein Dashboard',
  to: '/dashboard',
  items: [
    { label: 'Übersicht', to: '/dashboard', icon: ChartIcon },
  ],
};

// Menüeinträge eines Vereins – dieselbe Reihenfolge und Auswahl wie die Karten
// auf der Vereinsmanager-Übersicht (SchiessmeisterPage), damit Menü und
// Übersicht austauschbar sind. "Meldung" erscheint nur, solange eine Saison im
// Meldezeitraum ist – je offener Saison ein eigener Eintrag.
function buildVereinsItems(vereinId, meldungSaisons) {
  return [
    ...(Array.isArray(meldungSaisons) ? meldungSaisons : []).map((s) => ({
      label: `Mannschaften melden – Saison ${s.saisonId}`,
      to: `/verein/${vereinId}/meldung/${s.saisonId}`,
      icon: TeamIcon,
    })),
    { label: 'Mannschaftsübersicht', to: `/verein/${vereinId}/saison-mannschaften`, icon: TeamIcon },
    { label: 'Fehlende Wettkampfberichte', to: `/verein/${vereinId}/fehlende-berichte`, icon: AlertIcon },
    { label: 'Kalender', to: `/verein/${vereinId}/kalender`, icon: CalendarIcon },
    { label: 'Schützen', to: `/verein/${vereinId}/schuetzen`, icon: PersonIcon },
    { label: 'Schießstände', to: `/verein/${vereinId}/orte`, icon: LocationIcon },
    { label: 'Daten für elektronische Schießstände', to: `/verein/${vereinId}/elektronische-staende`, icon: MonitorIcon },
  ];
}

function buildVereinsSections(rollenLabel, vereine, meldungSaisons) {
  if (!vereine || vereine.length === 0) return [];

  if (vereine.length === 1) {
    const v = vereine[0];
    return [{
      label: rollenLabel,
      items: [
        { label: v.name || `Verein ${v.id}`, to: `/verein/${v.id}`, icon: HomeIcon },
        ...buildVereinsItems(v.id, meldungSaisons),
      ],
    }];
  }

  return vereine.map((v) => ({
    label: v.name || `Verein ${v.id}`,
    items: [
      { label: 'Übersicht', to: `/verein/${v.id}`, icon: HomeIcon },
      ...buildVereinsItems(v.id, meldungSaisons),
    ],
  }));
}

function parseBerechtigungen() {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) return [];
    const part = token.split('.')[1];
    if (!part) return [];
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    const raw = payload['berechtigung'];
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : [raw];
    return list.map(b => {
      const idx = b.indexOf(':');
      const rollenId = parseInt(b.slice(0, idx), 10);
      const vereinIdStr = b.slice(idx + 1);
      const vereinId = vereinIdStr ? parseInt(vereinIdStr, 10) : null;
      return { rollenId, vereinId };
    }).filter(b => !isNaN(b.rollenId));
  } catch {
    return [];
  }
}

function getSaisonLabel(saisonId) {
  if (saisonId == null || saisonId === '') return 'Aktuelle Saison';
  const id = Number(saisonId);
  if (!Number.isFinite(id)) return String(saisonId);
  return `${id}/${id + 1}`;
}

function getCurrentSaison(saisons) {
  const sorted = [...(Array.isArray(saisons) ? saisons : [])].sort((a, b) => Number(b?.saisonId ?? 0) - Number(a?.saisonId ?? 0));
  const selectedSaison = sorted.find((s) => Number(s?.status) === 30) ?? null;
  return selectedSaison
    ? {
        saisonId: selectedSaison.saisonId,
        label: getSaisonLabel(selectedSaison.saisonId),
        path: `/saison/${selectedSaison.saisonId}`,
      }
    : null;
}

function buildOwnTeamItems(scopedBerechtigungen, activeSaison) {
  const rows = Array.isArray(scopedBerechtigungen) ? scopedBerechtigungen : [];
  const activeSaisonId = Number(activeSaison?.saisonId);
  const seen = new Set();
  const items = [];

  rows.forEach((m) => {
    const teamId = Number(m?.mannschaftId ?? m?.mannschaftsId ?? m?.id);
    if (!Number.isFinite(teamId) || teamId <= 0 || seen.has(teamId)) return;

    const teamSaisonId = Number(m?.mannschaftSaisonId ?? m?.saisonId ?? m?.seasonId ?? m?.saison?.saisonId);
    if (Number.isFinite(activeSaisonId) && Number.isFinite(teamSaisonId) && teamSaisonId !== activeSaisonId) return;

    seen.add(teamId);
    const nummer = m?.mannschaftNummer ?? m?.mannschaftsnummer ?? m?.mannschaftsNummer ?? m?.nummer;
    const label = m?.name ?? m?.bezeichnung ?? (nummer != null ? `Mannschaft ${nummer}` : `Mannschaft ${teamId}`);
    items.push({
      label,
      to: `/mannschaft/${teamId}`,
      icon: TeamIcon,
    });
  });

  return items.sort((a, b) => a.label.localeCompare(b.label, 'de', { sensitivity: 'base', numeric: true }));
}

function AppNavbar({ currentUser, onLogout, showEmailReminder, onDismissEmailReminder }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [saisons, setSaisons] = useState([]);
  const [scopedBerechtigungen, setScopedBerechtigungen] = useState([]);
  const [oldSaisonsOpen, setOldSaisonsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    Saisons.getAll()
      .then((data) => {
        if (!cancelled) setSaisons(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setSaisons([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const schuetzenId = Number(currentUser?.schuetzenId);
    if (!Number.isFinite(schuetzenId) || schuetzenId <= 0) {
      setScopedBerechtigungen([]);
      return () => {
        cancelled = true;
      };
    }

    Schuetzen.getEigeneBerechtigungen()
      .then((data) => {
        if (!cancelled) setScopedBerechtigungen(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setScopedBerechtigungen([]);
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser?.schuetzenId]);

  const activeSaison = useMemo(
    () => getCurrentSaison(saisons),
    [saisons]
  );

  function isRouteActive(route) {
    if (!route) return false;
    if (location.pathname === route) return true;
    if (route.startsWith('/saison/') && location.pathname.startsWith(`${route}/`)) return true;
    if (route === '/admin' || route === '/saison' || route === '/allgemein') return false;
    return location.pathname.startsWith(route);
  }

  function isNavNodeActive(node) {
    if (isRouteActive(node.to)) return true;
    return (node.items ?? []).some(isNavNodeActive);
  }

  function isOldSaisonsNode(node) {
    return node.label === 'Alte Saisons';
  }

  function isOwnTeamsNode(node) {
    return node.label === 'Eigene Mannschaften';
  }

  function renderNavNode(node, level = 0) {
    if (node.separator) {
      return <div key={node.key} role="separator" className="mx-3 my-1.5 border-t border-slate-100" />;
    }
    if (node.items?.length) {
      const expanded = isOldSaisonsNode(node)
        ? (oldSaisonsOpen || isNavNodeActive(node))
        : isOwnTeamsNode(node)
          ? true
          : isNavNodeActive(node);
      const toggleOldSaisons = () => setOldSaisonsOpen((current) => !current);
      return (
        <div key={`${node.label}-${level}`} className={level > 0 ? 'mt-1 pl-3 border-l border-slate-100' : ''}>
          {node.to ? (
            <Link
              to={node.to}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center justify-between rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-widest transition-colors hover:bg-slate-50 ${level > 0 ? 'text-slate-500' : 'text-slate-400'}`}
            >
              {node.label}
              <ChevronDownIcon className={`h-3 w-3 transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`} />
            </Link>
          ) : isOldSaisonsNode(node) ? (
            <button
              type="button"
              onClick={toggleOldSaisons}
              className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-widest transition-colors hover:bg-slate-50 ${level > 0 ? 'text-slate-500' : 'text-slate-400'}`}
            >
              {node.label}
              <ChevronDownIcon className={`h-3 w-3 transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`} />
            </button>
          ) : (
            <div className={`px-3 py-2 text-xs font-semibold uppercase tracking-widest ${level > 0 ? 'text-slate-500' : 'text-slate-400'}`}>
              {node.label}
            </div>
          )}
          {expanded && (
            <div className="space-y-0.5">
              {node.items.map((child) => renderNavNode(child, level + 1))}
            </div>
          )}
        </div>
      );
    }

    const Icon = node.icon;
    const active = isRouteActive(node.to);
    return (
      <Link
        key={node.to}
        to={node.to}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          active
            ? 'nav-link-active'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        } ${level > 0 ? 'ml-3 pl-6' : ''}`}
      >
        {Icon ? <Icon className={`h-4 w-4 shrink-0 ${active ? '' : 'text-slate-400'}`} /> : null}
        {node.label}
      </Link>
    );
  }

  const navSections = useMemo(() => {
    const rollenId = currentUser?.rollenId ?? null;
    const vereine = currentUser?.vereine ?? [];
    const vereineMap = Object.fromEntries(vereine.map(v => [v.id, v]));
    const berechtigungen = parseBerechtigungen();
    const sections = [];

    if (currentUser?.schuetzenId) {
      sections.push(DASHBOARD_SECTION);
    }

    const isAdmin = rollenId === ROLLE_ADMINISTRATOR
      || berechtigungen.some(b => b.rollenId === ROLLE_ADMINISTRATOR);
    if (isAdmin) {
      sections.push(ADMIN_SECTION);
    }

    const schiessmeisterVereineIds = berechtigungen
      .filter(b => b.rollenId === ROLLE_SCHIESSMEISTER && b.vereinId != null)
      .map(b => b.vereinId);
    if (schiessmeisterVereineIds.length > 0) {
      const smVereine = schiessmeisterVereineIds.map(id => vereineMap[id]).filter(Boolean);
      const meldungSaisons = [...(Array.isArray(saisons) ? saisons : [])]
        .filter(s => Number(s?.status) === SAISON_STATUS_MELDUNG)
        .sort((a, b) => Number(b?.saisonId ?? 0) - Number(a?.saisonId ?? 0));
      sections.push(...buildVereinsSections('Vereinsmanager', smVereine, meldungSaisons));
    }

    // Wettkampfverwalter: sichtbar für alle, die irgendwo ein Ergebnis eintragen dürfen
    // (Administrator, Wettkampfmanager eines Vereins oder Schießmeister einer Mannschaft).
    const isWettkampfverwalter = isAdmin
      || berechtigungen.some(b => b.rollenId === ROLLE_WETTKAMPFMANAGER)
      || (Array.isArray(currentUser?.mannschaften) && currentUser.mannschaften.length > 0);
    if (isWettkampfverwalter) {
      sections.push(WETTKAMPFVERWALTER_SECTION);
    }

    const ownTeamItems = buildOwnTeamItems(scopedBerechtigungen, activeSaison);
    sections.push(buildSaisonSection(saisons, activeSaison, ownTeamItems));
    if (currentUser?.schuetzenId) {
      sections.push(MEINE_MANNSCHAFTEN_SECTION);
    }
    sections.push(ALLGEMEIN_SECTION);
    return sections;
  }, [currentUser, activeSaison, saisons, scopedBerechtigungen]);

  const initials = currentUser
    ? `${(currentUser.vorname || '')[0] || ''}${(currentUser.nachname || '')[0] || ''}`.toUpperCase()
    : '??';

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-slate-900/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 flex w-64 flex-col bg-white border-r border-slate-200 transition-transform duration-200 lg:static lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex h-16 items-center gap-3 px-5 border-b border-slate-200 shrink-0">
          <img
            src="/rwk-logo.png"
            alt="RWK Logo"
            className="h-10 w-10 object-contain shrink-0"
          />
          <div>
            <p className="text-sm font-semibold text-slate-900 leading-tight">RWK Manager</p>
            <p className="text-xs text-slate-400">Diözesanverband Paderborn</p>
          </div>
          <button
            className="ml-auto lg:hidden btn-ghost btn-icon"
            onClick={() => setSidebarOpen(false)}
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {navSections.map((section) => {
            const expanded = isNavNodeActive(section);
            const overviewTo = section.to ?? section.items[0]?.to ?? '#';
            return (
              <div key={section.label}>
                <Link
                  to={overviewTo}
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center justify-between rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-widest text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  {section.label}
                  <ChevronDownIcon className={`h-3 w-3 transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`} />
                </Link>
                {expanded && (
                  <div className="mt-0.5 space-y-0.5">
                    {section.items.map((item) => renderNavNode(item))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="border-t border-slate-200 p-3">
          <Link
            to="/profil"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-slate-50 transition-colors"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-brand-700 text-xs font-semibold shrink-0">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-900">
                {currentUser?.vorname} {currentUser?.nachname}
              </p>
              <p className="truncate text-xs text-slate-400">Mein Profil</p>
            </div>
          </Link>
          <button
            onClick={onLogout}
            title="Abmelden"
            className="mt-1 w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
          >
            <LogoutIcon className="h-4 w-4 shrink-0" />
            Abmelden
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
        {/* Topbar (mobile) */}
        <header className="flex h-16 items-center gap-4 border-b border-slate-200 bg-white px-4 lg:hidden shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
          >
            <MenuIcon className="h-5 w-5" />
          </button>
          <p className="text-sm font-semibold text-slate-900">RWK Manager</p>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {showEmailReminder && (
            <div className="flex items-center gap-3 bg-amber-50 border-b border-amber-200 px-4 sm:px-6 lg:px-8 py-3 text-sm text-amber-800">
              <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="flex-1">
                Keine E-Mail-Adresse hinterlegt.{' '}
                <a href="/profil" className="font-medium underline hover:text-amber-900">
                  Jetzt im Profil eintragen
                </a>
              </span>
              <button
                onClick={onDismissEmailReminder}
                className="shrink-0 rounded p-1 hover:bg-amber-100 transition-colors"
                title="Hinweis schließen"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            {/* Nachgeladene Seiten: Navigationsleiste bleibt stehen, nur der
                Inhalt zeigt kurz eine Ladeanzeige. */}
            <Suspense fallback={<SeiteLaedt />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>

      {/* Offene Mitteilungen erscheinen unabhaengig von der Seite. Die
          Komponente bleibt ueber Seitenwechsel hinweg montiert -- weggeklickte
          Mitteilungen kommen damit erst beim naechsten Aufruf der Anwendung
          wieder, nicht schon beim naechsten Menuklick. */}
      <MitteilungenPopup />
    </div>
  );
}

// ---- Inline SVG Icons ----

function GridIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}
function BuildingIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
    </svg>
  );
}
function PersonIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function SlidersIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" />
      <circle cx="8" cy="6" r="2" fill="currentColor" stroke="none" />
      <circle cx="16" cy="12" r="2" fill="currentColor" stroke="none" />
      <circle cx="10" cy="18" r="2" fill="currentColor" stroke="none" />
    </svg>
  );
}
function UsersIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function CalendarIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
function HomeIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}
function TeamIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function LogoutIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
function XIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function MenuIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}
function InfoIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
    </svg>
  );
}
// eslint-disable-next-line no-unused-vars
function DocumentIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}
function AboutIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}
function FolderIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
    </svg>
  );
}
function EuroIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 7a6 6 0 1 0 0 10" />
      <line x1="4" y1="10" x2="14" y2="10" />
      <line x1="4" y1="14" x2="14" y2="14" />
    </svg>
  );
}
function ShieldIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l7 3v6c0 4.5-3 8.3-7 9-4-.7-7-4.5-7-9V6l7-3z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}
function HistoryIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <polyline points="3 4 3 9 8 9" />
      <polyline points="12 7 12 12 15 14" />
    </svg>
  );
}
function LinkIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7L12 19" />
    </svg>
  );
}
function MailIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <polyline points="3 7 12 13 21 7" />
    </svg>
  );
}
function BellIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
function ChevronDownIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
function MonitorIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
      <circle cx="12" cy="10.5" r="2.5" />
    </svg>
  );
}
function AlertIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
function LocationIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M12 2C8.686 2 6 4.686 6 8c0 5.25 6 13 6 13s6-7.75 6-13c0-3.314-2.686-6-6-6z" />
      <circle cx="12" cy="8" r="2" />
    </svg>
  );
}
function TargetIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
    </svg>
  );
}
function ChartIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}

export default AppNavbar;
